import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Alert, AlertDescription } from '../ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { useCan } from '../../contexts/PermissionsContext'
import { usePutRentReviewTerms, useRentReviewTerms } from '../../api/leaseRent'

type Formula = Record<string, string | number | null | undefined>

const FIELDS = ['index_code', 'reference_quarter', 'base_period', 'base_value', 'interval_months', 'anniversary_date', 'floor_pct', 'cap_pct'] as const

/** The typed review formula of the lease: shown as extracted until a person confirms it. */
export function ReviewTermsCard({ org, contractUid }: { org: string; contractUid: string }) {
  const { t } = useTranslation('contracts')
  const canWrite = useCan('contracts.write')
  const { data } = useRentReviewTerms(org, contractUid)
  const put = usePutRentReviewTerms()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<string[]>([])

  const value = (data?.value ?? null) as Formula | null
  if (!value && !canWrite) return null

  const write = async (body: Formula) => {
    try {
      await put.mutateAsync({ params: { path: { orgCode: org, contractUid } }, body: body as never })
      setEditing(false)
      setErrors([])
    } catch (e) {
      const p = e as { title?: string; fields?: { name: string; message: string }[] }
      setErrors((p.fields ?? []).map((f) => `${f.name}: ${f.message}`).concat(p.fields?.length ? [] : [p.title ?? t('lease.rent_schedule.save_failed')]))
    }
  }

  const startEdit = () => {
    setForm(Object.fromEntries(FIELDS.map((f) => [f, value?.[f] === null || value?.[f] === undefined ? '' : String(value[f])])))
    setEditing(true)
  }

  const submit = () => {
    const num = (k: string) => (form[k] === '' ? undefined : Number(form[k]))
    const body: Formula = {
      index_code: form.index_code,
      base_period: form.base_period,
      base_value: num('base_value'),
      interval_months: num('interval_months'),
    }
    if (form.reference_quarter) body.reference_quarter = num('reference_quarter')
    if (form.anniversary_date) body.anniversary_date = form.anniversary_date
    if (form.floor_pct) body.floor_pct = num('floor_pct')
    body.cap_pct = form.cap_pct ? num('cap_pct') : null
    void write(body)
  }

  const check = data?.base_value_check
  return (
    <Card data-testid="review-terms-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">{t('lease.rent_schedule.review_title')}</CardTitle>
        {value && (
          <Badge variant={data?.approved ? 'success' : 'warning'} data-testid="review-terms-status">
            {data?.approved ? t('lease.rent_schedule.review_confirmed') : t('lease.rent_schedule.review_unconfirmed')}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {!value && !editing && <p className="text-sm text-muted-foreground">{t('lease.rent_schedule.review_none')}</p>}
        {value && !editing && (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm" data-testid="review-terms-value">
            {FIELDS.filter((f) => value[f] !== null && value[f] !== undefined).map((f) => (
              <div key={f} className="flex justify-between gap-2">
                <dt className="text-muted-foreground">{t(`lease.rent_schedule.review_fields.${f}` as any)}</dt>
                <dd className="tabular-nums" data-testid={`review-terms-${f}`}>{String(value[f])}</dd>
              </div>
            ))}
          </dl>
        )}
        {value && check && (
          <p className="text-sm" data-testid="review-terms-base-check" data-check={check}>
            {t(`lease.rent_schedule.base_check.${check}` as any, { value: data?.index_value })}
          </p>
        )}
        {data?.source_document_uid && (
          <Link to="/$org/documents/$docUid" params={{ org, docUid: data.source_document_uid }} className="text-sm underline"
            data-testid="review-terms-source-link">
            {t('lease.rent_schedule.review_source')}
          </Link>
        )}
        {editing && (
          <div className="grid grid-cols-2 gap-3" data-testid="review-terms-form">
            {FIELDS.map((f) => (
              <div key={f} className="space-y-1">
                <Label htmlFor={`review-${f}`}>{t(`lease.rent_schedule.review_fields.${f}` as any)}</Label>
                <Input id={`review-${f}`} value={form[f] ?? ''} data-testid={`review-terms-input-${f}`}
                  type={f === 'anniversary_date' ? 'date' : 'text'}
                  onChange={(e) => setForm({ ...form, [f]: e.target.value })} />
              </div>
            ))}
          </div>
        )}
        {errors.length > 0 && (
          <Alert variant="destructive" data-testid="review-terms-error">
            <AlertDescription>{errors.map((m) => <div key={m}>{m}</div>)}</AlertDescription>
          </Alert>
        )}
        {canWrite && (
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button size="sm" onClick={submit} disabled={put.isPending} data-testid="review-terms-save-button">{t('lease.rent_schedule.save')}</Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} data-testid="review-terms-cancel-button">{t('lease.rent_schedule.cancel')}</Button>
              </>
            ) : (
              <>
                {value && !data?.approved && (
                  <Button size="sm" onClick={() => void write(value)} disabled={put.isPending} data-testid="review-terms-confirm-button">
                    {t('lease.rent_schedule.review_confirm')}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={startEdit} data-testid="review-terms-edit-button">
                  {value ? t('lease.rent_schedule.edit') : t('lease.rent_schedule.review_add')}
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
